import './RoleStageActionFlows.scss';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import { ThankYouTemplate } from 'components/organisms/ThankYouTemplate/ThankYouTemplate';
import { ThankYouPageContentType } from 'components/organisms/ThankYouTemplate/ThankYouTemplate.enums';
import {
  SupportedLocale,
  useStartCognitiveTestIntegrityMonitoringSessionMutation,
  useUserQuery,
} from 'generated/graphql';
import { useIntegrityCamera } from 'hooks/useIntegrityCamera';
import { StartIntegritySession } from 'hooks/useIntegrityMonitoring';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@spotted-zebra-uk/ui-components';
import config, {
  withIntegrityMonitoringNote,
} from 'views/Test/TestInfo/TestInfo.constants';
import { convertExtraTimeValue } from 'views/Test/Tests/helpers/extraTime';
import CognitiveTestRunner from './CognitiveTestRunner';
import {
  actionCandidateStatusToTestStatus,
  cognitiveKindToTestKind,
} from './RoleStageActionFlows.helpers';
import { useOnlineAssessmentIntegrityMonitoring } from './useOnlineAssessmentIntegrityMonitoring';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';
import { getCompanyLogoUrl } from 'views/CandidatePortal/CandidatePortal.helpers';

/**
 * intro → (practice intro → practice run → practice complete) → real run.
 * The practice legs replace the legacy /tp-callback/practice route with
 * in-container state; every cognitive kind has a practice run.
 */
type TPhase = 'intro' | 'practice' | 'practiceComplete' | 'test';

const RoleStageCognitiveTest: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { actionCandidateId } = useParams();
  const { role, stage, loading, exiting, actionsUrl, thankYouUrl } =
    useRoleStageActionsFlow();

  const [phase, setPhase] = useState<TPhase>('intro');
  const [showPracticeTestIntro, setShowPracticeTestIntro] = useState(false);

  const userQueryResponse = useUserQuery();
  const assessmentLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  const action = stage?.actions.cognitiveTestActions.find(
    cognitiveTestAction => cognitiveTestAction.id === actionCandidateId
  );

  // Camera-based integrity monitoring (project toggle): the camera is
  // requested on the intro page; the session starts on grant and runs until
  // this flow unmounts (test finished or left), covering practice runs too.
  const monitoringEnabled =
    (stage?.isIntegrityMonitoringEnabled ?? false) && !!action;
  const camera = useIntegrityCamera(monitoringEnabled);
  const [startIntegritySession] =
    useStartCognitiveTestIntegrityMonitoringSessionMutation();
  const actionId = action?.id;
  const startSession = useCallback<StartIntegritySession>(
    async (input) => {
      if (!actionId) return null;
      const { data } = await startIntegritySession({
        variables: {
          cognitiveTestAssessmentStageActionCandidateId: actionId,
          input,
        },
      });
      return data?.startCognitiveTestIntegrityMonitoringSession ?? null;
    },
    [actionId, startIntegritySession]
  );
  useOnlineAssessmentIntegrityMonitoring({
    enabled: monitoringEnabled,
    stream: camera.stream,
    startSession,
  });

  // No matching cognitive test on this stage — nothing to run here.
  useEffect(() => {
    if (stage && !action) {
      navigate(actionsUrl);
    }
  }, [stage, action, navigate, actionsUrl]);

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !role || !stage || !action) {
    return null;
  }

  if (phase === 'intro') {
    const singleTestConfig = config[cognitiveKindToTestKind[action.kind]]({
      timeRequirement: action.allowedTimeSeconds,
      timePerQuestion: action.allowedSectionTimeSeconds,
      extraTimeAdded: action.timeModifierPercentage
        ? convertExtraTimeValue(action.timeModifierPercentage)
        : undefined,
    });
    const notes = monitoringEnabled
      ? withIntegrityMonitoringNote(singleTestConfig.notes)
      : singleTestConfig.notes;
    const MainIcon = singleTestConfig.icon;

    return (
      <Container className="TestInfoContainer">
        {!showPracticeTestIntro && (
          <NavbarWithBackLink
            to={actionsUrl}
            ariaLabel={t('assessment.returnToAllAssessmentActions')}
            label={t('assessment.actions')}
          />
        )}
        <HelmetAndPageAnnouncer
          pageTitle={t('test.introForTest', {
            actionName: singleTestConfig.title,
          })}
        />
        <TestIntro
          MainIcon={MainIcon}
          type={singleTestConfig.key}
          assessmentLocale={assessmentLocale}
          companyName={role.company.name}
          title={singleTestConfig.title}
          notes={notes}
          testStatus={actionCandidateStatusToTestStatus(action.status)}
          hasPractice
          onStart={() => setPhase('test')}
          onStartPracticeTest={() => setPhase('practice')}
          testTimePerQuestion={action.allowedSectionTimeSeconds}
          showPracticeTestIntro={showPracticeTestIntro}
          displayPracticeTestIntro={setShowPracticeTestIntro}
          cameraPermission={
            monitoringEnabled
              ? { granted: camera.granted, request: camera.request }
              : undefined
          }
        />
      </Container>
    );
  }

  if (phase === 'practiceComplete') {
    // Mirrors the legacy hiring-flow FinishedPracticeTestContent panel:
    // primary CTA starts the real assessment, secondary returns to the intro.
    return (
      <ThankYouTemplate
        onClick={() => setPhase('test')}
        mainText={t('stage.testCallback.youHaveCompletedYourPracticeTest')}
        contentType={ThankYouPageContentType.FINISHED_PRACTICE_STAGE}
        title={t('stage.testCallback.testPracticeCompleted')}
        buttonText={t('test.instructions.general.startNow')}
        companyLogoUrl={getCompanyLogoUrl(role.company)}
        companyName={role.company.name}
        isPracticeTest={true}
        onReturn={() => {
          setShowPracticeTestIntro(false);
          setPhase('intro');
        }}
      />
    );
  }

  return (
    <CognitiveTestRunner
      key={phase}
      actionCandidateId={action.id}
      kind={action.kind}
      isPracticeTest={phase === 'practice'}
      onFinishTest={() =>
        phase === 'practice' ? setPhase('practiceComplete') : navigate(thankYouUrl)
      }
    />
  );
};

export default RoleStageCognitiveTest;
